"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import "./route-loader.css";

/**
 * The route-transition indicator.
 *
 * WHAT IT WAS, AND WHY IT CHANGED (F69/F99, Phase 03).
 *
 * This used to render a fixed, full-viewport `bg-background/80
 * backdrop-blur-sm` overlay at `z-[9999]` with a 40KB Lottie animation in the
 * middle of it. Three separate problems, all of them on the busiest routes:
 *
 *   - It applied a FULL-VIEWPORT `backdrop-filter` at exactly the moment the
 *     main thread is busiest — the compositor re-samples the entire page while
 *     the next route is still being fetched and rendered. `pages/HOME.md` §24
 *     names removing the navigation-time full-viewport blur; Phase 03 §20
 *     states the constraint as "no full-viewport `backdrop-filter` during
 *     navigation".
 *   - It was the FIRST of three loading appearances per navigation onto the
 *     dashboard (overlay → route-level Lottie → in-component skeletons), which
 *     is the F99 finding. It communicated nothing about the incoming page and
 *     reserved no layout.
 *   - `z-[9999]` was one of thirteen ad-hoc z tiers, above every documented
 *     one including toasts.
 *
 * WHAT IT IS NOW. The single route-transition mechanism the component
 * inventory prescribes: a 2px determinate-looking progress bar pinned to the
 * top of the viewport. It occludes nothing, blurs nothing, and does not block
 * the page the user is still looking at — which matters, because a navigation
 * that turns out to be slow leaves them with a usable page instead of a
 * frosted one.
 *
 * IT IS NOT REPLACED BY A SKELETON. A route transition and a content load are
 * different events: this says "something is coming", the destination's own
 * component-level skeletons say "this part of it is still arriving". Merging
 * them is what produced three appearances in the first place.
 *
 * The click-detection logic below is unchanged: it is delegated, it respects
 * `preventDefault`, it ignores in-card controls and same-URL clicks, and it
 * has a watchdog so a prevented navigation cannot strand the indicator.
 */
export default function RouteLoader(t0) {
  const $ = _c(12);
  const {
    label
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsNavigating(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== pathname || $[2] !== searchParams) {
    t2 = [pathname, searchParams];
    $[1] = pathname;
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== isNavigating) {
    t3 = () => {
      if (!isNavigating) {
        return;
      }
      const id = setTimeout(() => setIsNavigating(false), 8000);
      return () => clearTimeout(id);
    };
    t4 = [isNavigating];
    $[4] = isNavigating;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const handleClick = e => {
        const target = (e.target as HTMLElement)?.closest?.("a[href]") as HTMLAnchorElement | null;
        if (!target) {
          return;
        }
        if (target.target === "_blank" || e.ctrlKey || e.metaKey || !target.href.startsWith(window.location.origin)) {
          return;
        }
        const interactive = (e.target as HTMLElement | null)?.closest?.("button, [role='button'], [role='link'], input, textarea, select");
        if (interactive && interactive !== target && target.contains(interactive)) {
          return;
        }
        const currentUrl = new URL(window.location.href);
        const targetUrl = new URL(target.href);
        if (currentUrl.pathname === targetUrl.pathname && currentUrl.search === targetUrl.search) {
          return;
        }
        setTimeout(() => {
          if (!e.defaultPrevented) {
            setIsNavigating(true);
          }
        }, 0);
      };
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  if (!isNavigating) {
    return null;
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div data-motion-keep={true} className="route-progress-bar" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== label) {
    t8 = <div className="route-progress" role="progressbar" aria-busy="true" aria-label={label}>{t7}</div>;
    $[10] = label;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
}